import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ListChecks, Plus, Send, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { QueryErrorCard } from "@/components/QueryErrorCard";
import { Guarded } from "@/components/Guarded";
import { EmptyState } from "@/components/fleet/EmptyState";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Pill } from "@/components/ui/Pill";
import { Skeleton } from "@/components/ui/Skeleton";
import { Tooltip } from "@/components/ui/Tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/Table";
import { useNow } from "@/components/ClockProvider";
import { useApiMutation, useApiQuery } from "@/hooks/useApi";
import { useRowNavigation } from "@/hooks/useRowNavigation";
import { del } from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { S, T } from "@/lib/strings";
import { cn, describeSchedule, timeAgo, truncate } from "@/lib/utils";
import type { Page, TaskRow } from "@/types/fleet";

export function Tasks() {
  const navigate = useNavigate();
  const now = useNow();
  const searchRef = useRef<HTMLInputElement>(null);
  const [pendingDelete, setPendingDelete] = useState<TaskRow | null>(null);

  const query = useApiQuery<Page<TaskRow>>(qk.tasks(), "/tasks");
  const rows = useMemo(() => query.data?.items ?? [], [query.data]);

  const nav = useRowNavigation({
    items: rows,
    getKey: (t) => t.id,
    onOpen: (t) => navigate(`/tasks/${t.id}`),
    searchRef,
  });

  const remove = useApiMutation<void, string>((id) => del<void>(`/tasks/${id}`), {
    invalidates: [qk.tasks()],
    successMessage: S.taskDeleted,
    errorTitle: S.mutationFailed,
    onSuccess: () => setPendingDelete(null),
  });

  return (
    <>
      <PageHeader
        title={S.tasksTitle}
        hint={S.tasksHint}
        actions={
          <Guarded permission="edit_task">
            <Button variant="primary" onClick={() => navigate("/tasks/new")}>
              <Plus size={13} />
              {S.newTask}
            </Button>
          </Guarded>
        }
      />

      {query.isError && (
        <QueryErrorCard error={query.error} onRetry={() => void query.refetch()} className="mb-3" />
      )}

      {query.isPending ? (
        <div className="space-y-1.5">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title="還沒有任務"
          body={S.noTasks}
          action={
            <Guarded permission="edit_task">
              <Button variant="primary" onClick={() => navigate("/tasks/new")}>
                <Plus size={13} />
                {S.newTask}
              </Button>
            </Guarded>
          }
        />
      ) : (
        <Table>
          <TableHead>
            <tr>
              <TableHeaderCell>{S.colTaskName}</TableHeaderCell>
              <TableHeaderCell>{S.colGoal}</TableHeaderCell>
              <TableHeaderCell className="w-40">{S.colSchedule}</TableHeaderCell>
              <TableHeaderCell className="w-24">{S.colAssignments}</TableHeaderCell>
              <TableHeaderCell className="w-24">{S.colUpdated}</TableHeaderCell>
              <TableHeaderCell className="w-24" />
            </tr>
          </TableHead>
          <TableBody>
            {rows.map((t, i) => (
              <TableRow
                key={t.id}
                ref={nav.registerRow(t.id)}
                tabIndex={0}
                onClick={() => navigate(`/tasks/${t.id}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") navigate(`/tasks/${t.id}`);
                }}
                className={cn(
                  "cursor-pointer",
                  nav.isCursor(i) &&
                    "bg-[rgba(113,112,255,0.08)] outline outline-1 -outline-offset-1 outline-[var(--color-accent)]",
                )}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-medium text-[var(--color-fg)]">{t.name}</span>
                    {!t.enabled && (
                      <Pill variant="muted" size="sm" dot={false}>
                        {S.disabled}
                      </Pill>
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-[var(--color-fg-faint)]">{t.id}</div>
                </TableCell>

                <TableCell>
                  <Tooltip content={t.goal}>
                    <span className="text-[12px]">{truncate(t.goal, 60)}</span>
                  </Tooltip>
                </TableCell>

                <TableCell>
                  <span className="text-[12px]">{describeSchedule(t.schedule)}</span>
                </TableCell>

                <TableCell>
                  <span className="text-[12px]">{T.assignmentCount(t.assignment_count ?? 0)}</span>
                </TableCell>

                <TableCell>
                  <span className="whitespace-nowrap text-[11px] text-[var(--color-fg-subtle)]">
                    {timeAgo(t.updated_at ?? t.updatedAt, now)}
                  </span>
                </TableCell>

                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <Guarded permission="dispatch_run">
                      <Link to={`/dispatch?task=${encodeURIComponent(t.id)}`}>
                        <Button size="sm" variant="ghost" aria-label={S.navDispatch}>
                          <Send size={12} />
                        </Button>
                      </Link>
                    </Guarded>
                    <Guarded permission="edit_task">
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={S.delete}
                        onClick={() => setPendingDelete(t)}
                      >
                        <Trash2 size={12} />
                      </Button>
                    </Guarded>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(v) => !v && setPendingDelete(null)}
        title={S.delete}
        description={S.deleteTaskConfirm}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>
              {S.cancel}
            </Button>
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() => pendingDelete && remove.mutate(pendingDelete.id)}
            >
              {S.delete}
            </Button>
          </>
        }
      >
        {pendingDelete && (
          <p className="text-sm text-[var(--color-fg-muted)]">
            {pendingDelete.name}
            {pendingDelete.assignment_count > 0 && (
              <span className="mt-1 block text-xs text-[#fcd34d]">
                此任務已指派給 {pendingDelete.assignment_count} 個目標，刪除後會一併移除。
              </span>
            )}
          </p>
        )}
      </Dialog>
    </>
  );
}
